import { IMatch, ILeague, ILeagueName, IContestant, IBaseState, IUser } from "../../typings/typings";
import { IApiLeagueRestriction } from "ng-league";
import { Match } from "./Match";

/**
 * abstract class for leagues - there is no abstract functionality in typescript, yet, so please do not
 * create a object of this class directly. Instead use the derived classes e.g. Cup
 */

export class League implements ILeague {
    public id: number;
    public type: string;
    public name: ILeagueName;
    public uri: string;
    public mode: string;
    public contestantType: string;
    public resultType: string;
    public teamSize: number;
    public skillLevel: string;
    public series: string;
    public rulesKey: string;
    public gameAccountType: string;
    public gameIntegration: string;
    public matchSetupAllowed: boolean;
    public matchMediaAllowed: boolean;
    public anticheatEnabled: boolean;
    public gameId: number;
    public signUp: any;
    public signOff: { enabled: boolean };
    public timeline: any;
    public restrictions: IApiLeagueRestriction[];
    public enabledFeatures: string[];
    public contestants;
    public state;
    public upcomingMatchesStates;
    public recentResultsCount;

    public currentContestant: IContestant;
    public currentState: IBaseState;

    public LegacyUrlService;

    // set time diff
    public TIMING_TOLERANCE = 2 * 1000;
    public RELOAD_LEAGUEDATA_SECONDS = 60;

    constructor(jsonData) {
        this.id = jsonData.id;
        this.type = jsonData.type;
        this.name = jsonData.name;
        this.uri = jsonData.uri;
        this.mode = jsonData.mode;
        this.contestantType = jsonData.contestantType;
        this.resultType = jsonData.resultType;
        this.teamSize = jsonData.teamSize;
        this.skillLevel = jsonData.skillLevel;
        this.series = jsonData.series;
        this.rulesKey = jsonData.rulesKey;
        this.gameAccountType = jsonData.gameAccountType;
        this.gameIntegration = jsonData.gameIntegration;
        this.matchSetupAllowed = jsonData.matchSetupAllowed;
        this.matchMediaAllowed = jsonData.matchMediaAllowed;
        this.gameId = jsonData.gameId;
        this.currentContestant = null;
        this.upcomingMatchesStates = {};
        this.recentResultsCount = 30;
        this.signUp = jsonData.signUp;
        this.timeline = jsonData.timeline;
        this.restrictions = jsonData.restrictions;
        this.enabledFeatures = jsonData.enabledFeatures;
        this.signOff = jsonData.signOff;
    }

    public setCurrentContestant(contestant: IContestant) {
        this.currentContestant = contestant;
    }

    public setCurrentState(currentState: IBaseState) {
        this.currentState = currentState;
    }

    /**
     * abstract function - needs to be overwritten in the exact implementation
     *
     * @returns {boolean}
     */
    public isFull() {
        return false;
    }

    public isCheckInAvailable() {
        return false;
    }

    public isSignUpAvailable() {
        return false;
    }

    public isPostSignUpAvailable() {
        return false;
    }

    public hasLateSignUp() {
        return false;
    }

    public hasSeason() {
        return false;
    }

    public getStartDate(): Date {
        return null;
    }

    public getNextDate(): Date {
        return null;
    }

    public isContestantNotSignedUp() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_NOT_SIGNED_UP;
    }

    public isContestantSignedUp() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_SIGNED_UP;
    }

    public contestantHasRanking() {
        return (
            this.currentContestant !== null &&
            this.currentContestant.state === this.currentState.CONTESTANT_STATE_FINISHED &&
            this.currentContestant.rank !== null
        );
    }

    public isContestantSignUpPending() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_SIGN_UP_PENDING;
    }

    public isContestantCheckedIn() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_CHECKED_IN;
    }

    public isContestantPlaying() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_PLAYING;
    }

    public isContestantFinished() {
        return this.currentContestant !== null && this.currentContestant.state === this.currentState.CONTESTANT_STATE_FINISHED;
    }

    public canContestantCheckin() {
        return this.isContestantSignedUp() && !this.isContestantCheckedIn() && !this.isFull();
    }

    public canContestantLateSignup() {
        return !this.isContestantSignUpPending() && !this.isContestantCheckedIn() && !this.isFull();
    }

    public canEnterResult() {
        return this.matchSetupAllowed;
    }

    public hasGameIntegration() {
        return this.gameIntegration !== null && this.gameIntegration !== "";
    }

    public isTeamLeague() {
        return this.contestantType === "team";
    }

    public getUri() {
        return this.uri;
    }

    public contestantHasRank() {
        if (typeof this.currentContestant === "undefined") {
            return false;
        }
        return this.currentContestant.rank !== null;
    }

    public needsGameAccount() {
        return this.gameAccountType !== "";
    }

    public getSecondsUntilDate(date) {
        // LeagueDate - ServerDate as microseconds
        const timeDiff = new Date(date).getTime() - new Date().getTime();
        // Make seconds, do not let it get below zero
        return Math.max(0, Math.ceil(timeDiff + this.TIMING_TOLERANCE) / 1000);
    }

    /**
     * Get seconds until the next-update
     */
    public getSecondsUntilNextUpdate(): number {
        return this.RELOAD_LEAGUEDATA_SECONDS;
    }

    public hasCountDown(): boolean {
        return true;
    }

    public getState(): string {
        return "";
    }

    public isInstantChallengerActive(): boolean {
        return false;
    }

    public getInstantChallengerInfo() {
        return undefined;
    }

    public isSignUpEnabled(): boolean {
        return this.signUp.enabled;
    }

    public isSignUpOpen(): boolean {
        if (this.timeline && this.timeline.signUp) {
            const signUp: { begin: string; end: string } = this.timeline.signUp;
            const now = new Date();

            return Boolean(now > new Date(signUp.begin) && (!signUp.end || now < new Date(signUp.end)));
        }

        return true;
    }

    public getUrlByTemplate(template, $filter, $scope) {
        switch (template) {
            case "ChallengeType":
                return "";
            case "Header":
                return $filter("sprintf")($scope.templateUrls.templateHeaderUrl, "league");
            case "Information":
                return $filter("sprintf")($scope.templateUrls.templateInformationUrl, "league");
            case "Match":
                return $filter("sprintf")($scope.templateUrls.templateMatchUrl, "league");
            case "Result":
                return $filter("sprintf")($scope.templateUrls.templateResultUrl, "league");
            case "SignupProgress":
                return $filter("sprintf")($scope.templateUrls.templateSignupProgressUrl, "league");
            case "UserAction":
                return $filter("sprintf")($scope.templateUrls.templateUserActionUrl, "league");
        }
        return "";
    }

    public refreshMatchData(
        league: ILeague,
        user: IUser,
        contestant: IContestant,
        LeagueMatchResource,
        $q: ng.IQService
    ): ng.IPromise<IMatch[]> {
        const deferred = $q.defer();
        if (user.id !== null) {
            LeagueMatchResource.getUpcomingMatch(league.id, user.id, this.upcomingMatchesStates).then(
                //success
                function(data) {
                    if (data.length > 0) {
                        const result = [];

                        while (data.length) {
                            const match: IMatch = new Match(data.pop(), user, league, contestant);
                            result.push(match);
                        }

                        deferred.resolve(result);
                    } else {
                        deferred.resolve([]);
                    }
                },
                //error
                function(reason) {
                    deferred.reject(reason);
                }
            );
        } else {
            deferred.reject("`user.id !== null` was false");
        }

        return deferred.promise as any;
    }

    public refreshResultData(
        league: ILeague,
        user: IUser,
        contestant: IContestant,
        LeagueMatchResource,
        $q: ng.IQService
    ): ng.IPromise<IMatch[]> {
        const defered = $q.defer();
        let results = [];
        if (user.id !== null) {
            LeagueMatchResource.getRecentResults(league.id, user.id, this.recentResultsCount).then(
                //success
                function(data) {
                    results = [];
                    if (data.length < 1) {
                        return defered.resolve([]);
                    }
                    while (data.length) {
                        results.push(new Match(data.pop(), user, league, contestant));
                    }

                    // Resolved
                    defered.resolve(results);
                },
                //error
                function(reason) {
                    // Reject
                    defered.reject(reason);
                }
            );
        } else {
            defered.reject("user.id !== null was false");
        }

        return defered.promise as any;
    }

    public getCurrentMatch(matches): IMatch {
        return matches.open[0];
    }

    public isRosterEnabled(): boolean {
        return this.isTeamLeague() && this.enabledFeatures.indexOf("roster") !== -1;
    }

    public isSignoffEnabled(): boolean {
        return this.signOff.enabled;
    }
}
